import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DsIconComponent, DsIconSize } from './ds-icon.component';
import { DsIconName } from './ds-icons';

export type DsBulletSize = 's' | 'm' | 'l' | 'xl';

@Component({
  selector: 'ds-bullet',
  standalone: true,
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      class="ds-bullet"
      [class.ds-bullet--s]="size === 's'"
      [class.ds-bullet--m]="size === 'm'"
      [class.ds-bullet--l]="size === 'l'"
      [class.ds-bullet--xl]="size === 'xl'"
      [style.background]="backgroundColor"
      [style.color]="textColor"
      [style.box-shadow]="resolvedBorderShadow"
      [attr.aria-hidden]="ariaHidden ? 'true' : null"
    >
      @if (iconName) {
        <ds-icon
          [name]="iconName"
          [size]="resolvedIconSize"
          [fill]="iconFill"
          [ariaLabel]="iconAriaLabel"
        />
      } @else {
        {{ text }}
      }
    </span>
  `,
  styles: [`
    :host {
      display: inline-flex;
      flex: 0 0 auto;
      line-height: 0;
    }

    .ds-bullet {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      min-inline-size: var(--_size);
      inline-size: max-content;
      block-size: var(--_size);
      padding-inline: var(--_padding-x);
      border: none;
      border-radius: var(--ds-bullet-border-radius);
      background: var(--ds-bullet-background);
      color: var(--ds-bullet-foreground);
      font-family: var(--ds-foundation-font-family-primary, 'Sura Sans', sans-serif);
      font-size: var(--_font-size);
      font-weight: var(--ds-bullet-font-weight);
      line-height: 0;
      text-align: center;
      text-box-trim: trim-both;
      text-box-edge: cap alphabetic;
      white-space: nowrap;
      box-sizing: border-box;
    }

    .ds-bullet--s {
      --_size: var(--ds-bullet-s-size);
      --_padding-x: var(--ds-bullet-s-padding-x);
      --_icon-size: var(--ds-bullet-s-icon-size);
      --_font-size: var(--ds-bullet-s-font-size);
      --_line-height: var(--ds-bullet-s-line-height);
    }

    .ds-bullet--m {
      --_size: var(--ds-bullet-m-size);
      --_padding-x: var(--ds-bullet-m-padding-x);
      --_icon-size: var(--ds-bullet-m-icon-size);
      --_font-size: var(--ds-bullet-m-font-size);
      --_line-height: var(--ds-bullet-m-line-height);
    }

    .ds-bullet--l {
      --_size: var(--ds-bullet-l-size);
      --_padding-x: var(--ds-bullet-l-padding-x);
      --_icon-size: var(--ds-bullet-l-icon-size);
      --_font-size: var(--ds-bullet-l-font-size);
      --_line-height: var(--ds-bullet-l-line-height);
    }

    .ds-bullet--xl {
      --_size: var(--ds-bullet-xl-size);
      --_padding-x: var(--ds-bullet-xl-padding-x);
      --_icon-size: var(--ds-bullet-xl-icon-size);
      --_font-size: var(--ds-bullet-xl-font-size);
      --_line-height: var(--ds-bullet-xl-line-height);
    }
  `],
})
export class DsBulletComponent {
  @Input() text: string | number = 1;
  @Input() size: DsBulletSize = 's';
  @Input() iconName: DsIconName | string = '';
  @Input() iconFill = false;
  @Input() iconAriaLabel = '';
  @Input() backgroundColor = 'var(--ds-bullet-background)';
  @Input() textColor = 'var(--ds-bullet-foreground)';
  @Input() borderColor = '';
  @Input() ariaHidden = false;

  get resolvedIconSize(): DsIconSize {
    return 'var(--_icon-size)';
  }

  get resolvedBorderShadow(): string | null {
    return this.borderColor ? `inset 0 0 0 var(--ds-bullet-border-width) ${this.borderColor}` : null;
  }
}
